/**
 * FeePanel - transfer fee + waiver + payment method.
 *
 * The fee is the backend-resolved transfer policy fee (from the eligibility
 * preflight's ``resolved_policy.fee_amount``, itself read from the warranty
 * template's ``transfer_fee``) - the same value the record snapshots at
 * creation, so the wizard and the saved record always agree (WPS-157 §2).
 *
 * Waiver UI is shown conditionally; future Phase 1b gates this behind
 * the `transfers.waive_fee` permission (interim: always shown with a
 * note about audit logging).
 */

import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { Check, CreditCard, BadgeDollarSign } from 'lucide-react'

// Three mutually-exclusive ways to settle the transfer fee. The Waive
// path requires an audited reason; the other two are payment methods
// with no extra capture.
type PaymentChoice = 'check' | 'online' | 'waive'

const CHOICES: {
  value: PaymentChoice
  label: string
  helper: string
  Icon: typeof Check
}[] = [
  { value: 'check', label: 'Check', helper: 'Mailed to office', Icon: Check },
  {
    value: 'online',
    label: 'Pay Online',
    helper: 'Stripe checkout link',
    Icon: CreditCard,
  },
  {
    value: 'waive',
    label: 'Waive Fee',
    helper: 'Reason required, audited',
    Icon: BadgeDollarSign,
  },
]

interface FeePanelProps {
  /** Backend-resolved transfer fee (from the preflight's resolved_policy). */
  baseFee: number
  feeWaived: boolean
  feeWaivedReason: string
  paymentMethod: string
  onFeeWaivedChange: (waived: boolean) => void
  onFeeWaivedReasonChange: (reason: string) => void
  onPaymentMethodChange: (method: string) => void
}

export function FeePanel({
  baseFee,
  feeWaived,
  feeWaivedReason,
  paymentMethod,
  onFeeWaivedChange,
  onFeeWaivedReasonChange,
  onPaymentMethodChange,
}: FeePanelProps) {
  const hasFee = baseFee > 0

  // Derive the single active choice from the two backend fields:
  //   feeWaived=true              -> 'waive'
  //   payment_method='check'      -> 'check'
  //   payment_method='online'     -> 'online'
  //   neither                     -> null (user hasn't picked yet)
  const activeChoice: PaymentChoice | null = feeWaived
    ? 'waive'
    : paymentMethod === 'check'
      ? 'check'
      : paymentMethod === 'online'
        ? 'online'
        : null

  // Single setter that maps the 3-way pick back onto the two fields the
  // backend understands. Picking 'waive' clears the payment method so
  // the wizard never sends both at once.
  const handleSelect = (choice: PaymentChoice) => {
    if (choice === 'waive') {
      onFeeWaivedChange(true)
      onPaymentMethodChange('')
    } else {
      onFeeWaivedChange(false)
      onFeeWaivedReasonChange('')
      onPaymentMethodChange(choice)
    }
  }

  return (
    <div className="max-w-[640px]">
      <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
        Transfer Fee &amp; Payment
      </h2>
      <p className="mb-6 text-[13px] font-light text-[var(--ink-3)]">
        Fee is set by policy based on the warranty types being transferred.
      </p>

      {/* Fee headline */}
      <div className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-6"
        data-tour="transfer-fee"
      >
        <div className="flex items-baseline justify-between">
          <p className="clara-section-label">Transfer Fee</p>
          {!hasFee && (
            <Badge
              variant="secondary"
              className="rounded-full bg-[var(--status-ok-bg)] text-[10px] uppercase tracking-[0.08em] text-[var(--status-ok)]"
            >
              No fee required
            </Badge>
          )}
        </div>
        <div className="mt-3 flex items-baseline gap-3">
          <p
            className={`font-heading text-[32px] font-light ${
              feeWaived ? 'text-[var(--ink-3)] line-through' : 'text-[var(--ink)]'
            }`}
          >
            ${baseFee.toFixed(2)}
          </p>
          {feeWaived && (
            <Badge
              variant="secondary"
              className="rounded-full bg-[var(--status-warn-bg)] text-[10px] uppercase tracking-[0.08em] text-[var(--status-warn)]"
            >
              Waived
            </Badge>
          )}
        </div>
        <p className="mt-2 text-[11px] font-light text-[var(--ink-3)]">
          {hasFee
            ? 'Extended warranty transfer fee per OC policy.'
            : 'Standard warranties transfer without a fee.'}
        </p>
      </div>

      {/* Three-way payment choice - Check, Pay Online, Waive. One pick,
          mutually exclusive, captured in the same field set the backend
          already accepts. */}
      {hasFee && (
        <div className="mb-4">
          <Label className="clara-section-label mb-3 block">
            How is the fee being settled?
          </Label>
          <div
            role="radiogroup"
            aria-label="Payment choice"
            className="grid grid-cols-1 gap-2 sm:grid-cols-3"
          >
            {CHOICES.map(({ value, label, helper, Icon }) => {
              const isActive = activeChoice === value
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => handleSelect(value)}
                  data-testid={`payment-choice-${value}`}
                  className={`flex flex-col items-start gap-2 rounded-[8px] border-[0.5px] p-4 text-left transition-colors ${
                    isActive
                      ? 'border-[var(--green)] bg-[var(--green-bg-muted)]'
                      : 'border-[var(--rule)] bg-[var(--bg-subtle)] hover:border-[var(--ink-3)]'
                  }`}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <Icon
                      className={`h-3.5 w-3.5 ${
                        isActive ? 'text-[var(--green)]' : 'text-[var(--ink-3)]'
                      }`}
                      strokeWidth={1.25}
                    />
                    {isActive && (
                      <span className="text-[10px] font-light uppercase tracking-[0.14em] text-[var(--green)]">
                        Selected
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-[13px] font-light ${
                      isActive ? 'text-[var(--ink)]' : 'text-[var(--ink-2)]'
                    }`}
                  >
                    {label}
                  </p>
                  <p className="text-[11px] font-light text-[var(--ink-3)]">
                    {helper}
                  </p>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Reason capture - only when Waive is active. Required by both
          the wizard validator and the backend serializer. */}
      {hasFee && activeChoice === 'waive' && (
        <div className="mt-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <Label className="clara-section-label mb-2 block">
            Reason for waiver <span className="text-[var(--green)]">*</span>
          </Label>
          <Input
            value={feeWaivedReason}
            onChange={(e) => onFeeWaivedReasonChange(e.target.value)}
            placeholder="e.g. Goodwill adjustment approved by Regional Manager"
            data-testid="fee-waive-reason"
          />
          <p className="mt-2 text-[11px] font-light text-[var(--ink-3)]">
            Fee waivers are audited. Only authorized roles can waive fees.
          </p>
        </div>
      )}

      {/* Online-payment helper - only when Pay Online is active. */}
      {hasFee && activeChoice === 'online' && (
        <p className="mt-2 text-[11px] font-light text-[var(--ink-3)]">
          A Stripe checkout link will be sent to the new owner after the
          transfer is approved.
        </p>
      )}
    </div>
  )
}
